import React from "react";
import { SelectorDisplayItemProps } from "../components/Selector";
import { Fetcher } from "../components/Fetcher";
import { LogEvent } from "../models/LogEvents";
import { FilterContainer } from "../components/FilterContainer";
import { DEFAULT_PAGING_SIZE, MIN_TABLE_HEIGHT, MAX_TABLE_HEIGHT } from "../constants";
import { Pagination } from "../components/Pagination";
import { TwilioDetailsLogListContainer } from "../components/TwilioDetailsLogListContainer";
import { makeStyles } from "@material-ui/core/styles";
import { grey } from "@material-ui/core/colors";
import { MessageCard } from "../components/MessageCard";
import { Typography } from "@material-ui/core";
import { getTwilioCallLogsPage } from "../services/api/twilioCallLogs.api";
import { TwilioCallsResponse, GetCallLogsRequest, CallLogsFilterObject } from "../models/TwilioModels";
import moment from "moment";
import { DateDisplay } from "../components/DateDisplay";
import queryString from "query-string";
import { store } from "../globalState/StateProvider";
import {
	setCallLogsFiltersLocalStorageValue,
	getCallLogsFiltersLocalStorageValue,
	resetCallLogsFiltersLocalStorageValue,
} from "../globalState/LocalStorage/TwilioLocalStorageAccessors";

interface CallLogsPageProps {
	// subscription id to fetch details
	subscriptionId: string;
	// product id used to fetch the subscription
	productId: string;
}

const useStyles = makeStyles((theme) => ({
	title: {
		marginBottom: theme.spacing(3),
	},
	tableWrapper: {
		minHeight: MIN_TABLE_HEIGHT,
		maxHeight: MAX_TABLE_HEIGHT,
		maxWidth: 1200,
		overflow: "auto",
		borderBottomColor: grey[100],
		borderBottomWidth: 1,
		borderBottomStyle: "solid",
	},
	tableTitle: {
		zIndex: 1,
	},
}));

const renderList = (
	callResponses: TwilioCallsResponse,
	classes: Record<any, string>,
	setPageNumber: (n: number) => void,
	setRowsPerPage: (n: number) => void,
	setPageUrl: (s: string) => void,
	pageNumber: number,
	rowsPerPage: number,
	subscriptionId: string,
	accountSid: string,
	productId: string,
) => {
	let events: LogEvent[] = [];
	if (callResponses.calls !== undefined) {
		events = callResponses.calls.map((call) => {
			const { callSid, startDate, endDate, message, ...innerProps } = call;

			return {
				sid: callSid,
				eventDate: moment(startDate).toISOString(),
				endDate: DateDisplay({ date: endDate, fontSize: 14 }),
				message: message,
				source: "callList",
				...innerProps,
			};
		});
	}

	function SetPage(newPageNumber: number) {
		if (newPageNumber > pageNumber) {
			setPageUrl(callResponses.nextPageUri);
		} else {
			setPageUrl(callResponses.prevPageUri);
		}
		setPageNumber(newPageNumber);
	}
	const query = queryString.parse(window.location.search);

	return (
		<div>
			{events.length > 0 || query.__sid !== undefined ? (
				<div>
					<div className={classes.tableWrapper}>
						<TwilioDetailsLogListContainer
							productId={productId}
							accountSid={accountSid}
							subscriptionId={subscriptionId}
							logs={events}
							pageName={"SubscriptionCallList"}
						></TwilioDetailsLogListContainer>
					</div>
					{events.length > 0 ? (
						<Pagination
							pageNumber={pageNumber}
							rowsPerPage={rowsPerPage}
							setPageFunction={SetPage}
							setRowsPerPageFunction={setRowsPerPage}
							currentPageLength={events.length}
						></Pagination>
					) : (
						<div></div>
					)}
				</div>
			) : (
				<MessageCard message="No Events found." />
			)}
		</div>
	);
};

//Statuses to be displayed in the status selector.
const directions: SelectorDisplayItemProps[] = [
	{
		displayString: "Any",
		valueString: "Any",
	},
	{
		displayString: "Inbound",
		valueString: "inbound",
	},
	{
		displayString: "Outbound Dial",
		valueString: "outbound-dial",
	},
	{
		displayString: "Outbound Api",
		valueString: "outbound-api",
	},
];

//Statuses to be displayed in the status selector.
const statuses: SelectorDisplayItemProps[] = [
	{
		displayString: "Any",
		valueString: "Any",
	},
	{
		valueString: "queued",
		displayString: "Queued",
	},
	{
		valueString: "ringing",
		displayString: "Ringing",
	},
	{
		valueString: "in-progress",
		displayString: "In-Progress",
	},
	{
		valueString: "completed",
		displayString: "Completed",
	},
	{
		valueString: "busy",
		displayString: "Busy",
	},
	{
		valueString: "failed",
		displayString: "Failed",
	},
	{
		valueString: "no-answer",
		displayString: "No-Answer",
	},
	{
		valueString: "canceled",
		displayString: "Canceled",
	},
];

/**
 * This is the page to display the call logs for Mso's.
 * @param props
 */
export const CallLogsPage = (props: CallLogsPageProps) => {
	const classes = useStyles();
	const { environments, subscription } = React.useContext(store);

	const environmentsList =
		subscription && subscription!.subscription!.subscriptionId === props.subscriptionId ? environments : [];

	const [pageNumber, setPageNumber] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGING_SIZE);
	const [pageURL, setPageURL] = React.useState("");
	const [update, forceUpdate] = React.useState(false);
	const filterObject: CallLogsFilterObject = getCallLogsFiltersLocalStorageValue(props.subscriptionId);

	function UpdateFilters(newFilterObject) {
		setCallLogsFiltersLocalStorageValue(props.subscriptionId, newFilterObject);
		setPageURL("");
		setPageNumber(0);
		forceUpdate(!update);
	}

	function ResetFilters() {
		setPageURL("");
		setPageNumber(0);
		forceUpdate(!update);
		return resetCallLogsFiltersLocalStorageValue(props.subscriptionId);
	}

	const request: GetCallLogsRequest = {
		startDate: filterObject.startDate,
		endDate: filterObject.endDate,
		accountSid:
			!filterObject.selectedEnvironment && environmentsList.length > 0
				? environmentsList[0].valueString!
				: filterObject.selectedEnvironment,
		duration: filterObject.duration,
		callSid: filterObject.searchSid,
		toNumber: filterObject.toNumber,
		fromNumber: filterObject.fromNumber,
		status: filterObject.status !== "Any" ? filterObject.status : "",
		direction: filterObject.direction !== "Any" ? filterObject.direction || "" : "",
		pageSize: rowsPerPage,
		pageUri: pageNumber !== 0 && pageURL !== "" ? pageURL : "",
	};

	function loadList() {
		if (environmentsList.length > 0) {
			return (
				<div>
					<Fetcher action={getTwilioCallLogsPage(request, props.productId, props.subscriptionId)}>
						{(response) =>
							renderList(
								response.data,
								classes,
								setPageNumber,
								setRowsPerPage,
								setPageURL,
								pageNumber,
								rowsPerPage,
								props.subscriptionId,
								filterObject.selectedEnvironment !== ""
									? filterObject.selectedEnvironment
									: environmentsList[0].valueString!,
								props.productId,
							)
						}
					</Fetcher>
				</div>
			);
		}
	}

	return (
		<div data-testid={"subscription-call-list-page"}>
			<Typography className={classes.title} variant="h4">
				Call Logs
			</Typography>
			{environmentsList.length > 0 && (
				<FilterContainer
					statusFilter={{ statuses: statuses, selectedStatus: filterObject.status }}
					callLogFilters={{
						selectedDirection: filterObject.direction,
						directions: directions,
						toNumber: filterObject.toNumber,
						fromNumber: filterObject.fromNumber,
						duration: filterObject.duration,
					}}
					environmentFilter={{
						environments: environmentsList,
						selectedEnvironment:
							filterObject.selectedEnvironment !== ""
								? filterObject.selectedEnvironment
								: environmentsList[0].valueString!,
					}}
					dateFilter={{
						startDate: filterObject.startDate,
						endDate: filterObject.endDate,
						refreshDateTimespan: filterObject.refreshDateTimespan,
					}}
					searchSid={filterObject.searchSid}
					onUpdate={UpdateFilters}
					onReset={ResetFilters}
				></FilterContainer>
			)}
			{loadList()}
		</div>
	);
};
